import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { SearchableSelect } from '@/shared/ui/searchable-select'
import { WeeklyScheduleGrid, ALL_DAYS_ON } from '@/shared/ui/weekly-schedule-grid'
import type { WeekdaySchedule } from '@/shared/ui/weekly-schedule-grid'
import { NoAccessNotice } from '@/shared/ui/no-access-notice'
import { Separator } from '@/shared/ui/separator'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { departmentsPath, unitPath } from '@/shared/lib/routes'
import type { WebDepartmentForm } from '@/shared/types/web'
import { useFollowParentSiteSchedulePreview } from '@/entities/organization/departments/useFollowParentSiteSchedulePreview'
import { ACCREDITATION_FIELDS } from '@/entities/organization/units/constants'
import {
  hasLicenseOrAccreditationValues,
  isBlankIdentifier,
  normalizeAccreditationForForm,
  snakeCaseAccreditation,
} from '@/entities/organization/units/licenseIdentifiers'

interface SiteOption {
  value: number
  label: string
}

interface DepartmentUnitRow {
  _key: number
  id: number | null
  name: string
  licenseNumber: string
  followParentSchedule: boolean
  customDaySchedule: WeekdaySchedule
  accreditation: Record<string, string>
  hasLinkedInstruments: boolean
}

interface DepartmentFormData {
  name: string
  siteId: number | null
  units: DepartmentUnitRow[]
}

// Client-only validation keys that are not persisted form fields. Inertia types
// form.errors by the data keys, so these synthetic keys are set via a cast.
type DepartmentClientErrors = Partial<
  Record<'base' | 'licenseOrAccreditation' | 'unitIndex', string>
>

interface DepartmentFormProps {
  sites: SiteOption[]
  initialData?: Partial<WebDepartmentForm>
  submitUrl: string
  submitMethod: 'post' | 'patch'
  customDaySchedule?: WeekdaySchedule
  parentDaySchedule?: WeekdaySchedule
  /** Client-side site seed (e.g. remembered New Department site); the server preview stays on `initialData.siteId`. */
  defaultSiteId?: number | null
  onSiteChange?: (siteId: number | null) => void
}

// A department cannot be created without at least one unit — mirrors the legacy
// "Add Department" form, which embeds the first unit's fields directly on the page.
let nextDepartmentUnitRowKey = 0
const makeDepartmentUnitRow = (customDaySchedule: WeekdaySchedule): DepartmentUnitRow => ({
  _key: nextDepartmentUnitRowKey++,
  id: null,
  name: '',
  licenseNumber: '',
  followParentSchedule: true,
  customDaySchedule: { ...customDaySchedule },
  accreditation: {},
  hasLinkedInstruments: false,
})

// Existing units of a department being edited are hydrated into editable rows so
// the form matches the create screen (legacy parity, REBUILD-411). Typelizer emits
// loosely-typed unit payloads, so we describe the fields we consume explicitly.
interface DepartmentFormUnitPayload {
  id: number
  name: string | null
  licenseNumber: string | null
  followParentSchedule: boolean | null
  accreditation: Record<string, unknown> | null
  customDaySchedule: WeekdaySchedule | null
  hasLinkedInstruments: boolean
}

const toDepartmentUnitRow = (unit: DepartmentFormUnitPayload): DepartmentUnitRow => ({
  _key: nextDepartmentUnitRowKey++,
  id: unit.id,
  name: unit.name ?? '',
  licenseNumber: unit.licenseNumber ?? '',
  followParentSchedule: Boolean(unit.followParentSchedule),
  customDaySchedule: unit.customDaySchedule ?? { ...ALL_DAYS_ON },
  accreditation: normalizeAccreditationForForm(unit.accreditation),
  hasLinkedInstruments: Boolean(unit.hasLinkedInstruments),
})

const firstUnitMissingLicenseIndex = (units: DepartmentUnitRow[]): number =>
  units.findIndex((row) => !hasLicenseOrAccreditationValues(row.licenseNumber, row.accreditation))

const firstUnitMissingNameIndex = (units: DepartmentUnitRow[]): number =>
  units.findIndex((row) => isBlankIdentifier(row.name))

const DepartmentForm = ({
  sites,
  initialData,
  submitUrl,
  submitMethod,
  customDaySchedule,
  parentDaySchedule,
  defaultSiteId,
  onSiteChange,
}: DepartmentFormProps) => {
  const canSubmit = useCan(
    Resources.DEPARTMENT,
    submitMethod === 'post' ? Actions.CREATE : Actions.UPDATE,
  )
  const isCreating = submitMethod === 'post'
  // REBUILD-386 (CR): create starts blank (or the site remembered this session), unlike
  // legacy's first-site preselect, so the user consciously picks the site new units
  // inherit. Still required on submit.
  const serverPreviewSiteId = (initialData?.siteId as number | null | undefined) ?? null
  const form = useForm<DepartmentFormData>({
    name: (initialData?.name as string | undefined) ?? '',
    siteId: defaultSiteId ?? serverPreviewSiteId,
    units: isCreating
      ? [makeDepartmentUnitRow(customDaySchedule ?? { ...ALL_DAYS_ON })]
      : ((initialData?.units as DepartmentFormUnitPayload[] | undefined) ?? []).map(
          toDepartmentUnitRow,
        ),
  })

  const anyUnitFollowsParent = form.data.units.some((row) => row.followParentSchedule)
  // Exhausted preview: bump to retry without requiring every nested unit Follow OFF.
  const [previewRetryNonce, setPreviewRetryNonce] = useState(0)
  // #new previews a blank site and #edit the department site. A client-seeded
  // defaultSiteId differs from that server preview, so the hook reloads it.
  const { effectiveParentPreview, parentPreviewPending, parentPreviewFailed } =
    useFollowParentSiteSchedulePreview({
      enabled: anyUnitFollowsParent,
      siteId: form.data.siteId,
      parentDaySchedule,
      initialSiteId: serverPreviewSiteId,
      retryNonce: previewRetryNonce,
    })

  const addUnit = () => {
    form.setData('units', [
      ...form.data.units,
      makeDepartmentUnitRow(customDaySchedule ?? { ...ALL_DAYS_ON }),
    ])
  }

  const removeUnit = (key: number) => {
    form.setData(
      'units',
      form.data.units.filter((row) => row._key !== key),
    )
  }

  // Removing an existing unit is a persisted soft-delete (linked instruments +
  // "keep at least one unit" rule), so it reuses the Units tab delete flow:
  // confirm, then DELETE /units/:id. New (unsaved) rows are just spliced out.
  const [pendingRemoval, setPendingRemoval] = useState<DepartmentUnitRow | null>(null)
  const activeUnitCount = form.data.units.length

  const handleRemoveClick = (row: DepartmentUnitRow) => {
    if (row.id === null) {
      removeUnit(row._key)
      return
    }
    setPendingRemoval(row)
  }

  const confirmExistingUnitRemoval = () => {
    const removed = pendingRemoval
    if (!removed?.id) return
    setPendingRemoval(null)

    // Optimistically drop the unit so the form reflects the change immediately
    // (Inertia reuses the mounted form, so a server round-trip alone would not
    // refresh useForm's units). The DELETE persists via the shared discard flow;
    // if the server rejects it (e.g. last-unit guard), restore the row.
    const previousUnits = form.data.units
    form.setData(
      'units',
      previousUnits.filter((row) => row._key !== removed._key),
    )
    router.delete(unitPath(removed.id), {
      preserveScroll: true,
      preserveState: true,
      onError: () => form.setData('units', previousUnits),
    })
  }

  const updateUnit = <Field extends keyof DepartmentUnitRow>(
    key: number,
    field: Field,
    value: DepartmentUnitRow[Field],
  ) => {
    form.setData(
      'units',
      form.data.units.map((row) => (row._key === key ? { ...row, [field]: value } : row)),
    )
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (isBlankIdentifier(form.data.name)) {
      form.clearErrors()
      form.setError('name', t('organization.departments.form.nameRequired'))
      document.getElementById('name')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    if (isCreating && !form.data.siteId) {
      form.clearErrors()
      form.setError('siteId', t('organization.departments.form.siteRequired'))
      document.getElementById('siteId')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    const missingNameIndex = firstUnitMissingNameIndex(form.data.units)
    if (missingNameIndex >= 0) {
      const failingUnit = form.data.units[missingNameIndex]
      form.clearErrors()
      form.setError({
        base: t('organization.units.form.nameRequired'),
        unitIndex: String(missingNameIndex),
      } as DepartmentClientErrors as Partial<Record<keyof DepartmentFormData, string>>)
      document
        .getElementById(`unit_name_${failingUnit._key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    const missingLicenseIndex = firstUnitMissingLicenseIndex(form.data.units)
    if (missingLicenseIndex >= 0) {
      const failingUnit = form.data.units[missingLicenseIndex]
      form.clearErrors()
      form.setError({
        licenseOrAccreditation: t('organization.units.form.licenseOrAccreditationRequired'),
        unitIndex: String(missingLicenseIndex),
      } as DepartmentClientErrors as Partial<Record<keyof DepartmentFormData, string>>)
      document
        .getElementById(`unit_license_${failingUnit._key}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    const departmentPayload = (data: DepartmentFormData) => ({
      name: data.name.trim(),
      site_id: data.siteId,
    })
    const unitsPayload = (data: DepartmentFormData) =>
      data.units.map((row) => ({
        // Existing units carry their id so the backend updates rather than creates.
        ...(row.id ? { id: row.id } : {}),
        name: row.name.trim(),
        license_number: row.licenseNumber.trim(),
        follow_parent_schedule: row.followParentSchedule,
        custom_day_schedule: row.customDaySchedule,
        accreditation: snakeCaseAccreditation(row.accreditation),
      }))
    if (isCreating) {
      form.transform((data) => ({
        department: departmentPayload(data),
        units: unitsPayload(data),
      }))
      form.post(submitUrl)
    } else {
      form.transform((data) => ({
        department: departmentPayload(data),
        units: unitsPayload(data),
      }))
      form.patch(submitUrl)
    }
  }

  const genericErrors = form.errors as Record<string, string | undefined>
  const baseError = genericErrors.base
  const licenseOrAccreditationError = genericErrors.licenseOrAccreditation
  const unitErrorIndex = genericErrors.unitIndex ? Number(genericErrors.unitIndex) : null

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('space-y-4', isCreating ? 'max-w-2xl' : 'max-w-xl')}
      noValidate
    >
      <div className="space-y-1.5">
        <Label htmlFor="name">{t('organization.departments.form.name')} *</Label>
        <Input
          id="name"
          value={form.data.name}
          onChange={(event) => form.setData('name', event.target.value)}
          placeholder={t('organization.departments.form.namePlaceholder')}
          required
        />
        {form.errors.name && <p className="text-sm text-destructive">{form.errors.name}</p>}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="siteId">
          {t('organization.departments.form.site')}
          {isCreating ? ' *' : ''}
        </Label>
        <SearchableSelect
          id="siteId"
          value={form.data.siteId?.toString() ?? ''}
          onValueChange={(value) => {
            const nextSiteId = value ? Number(value) : null
            form.setData('siteId', nextSiteId)
            onSiteChange?.(nextSiteId)
          }}
          placeholder={t('organization.departments.form.site')}
          aria-required={isCreating}
          options={sites.map((site) => ({ value: site.value.toString(), label: site.label }))}
        />
        {form.errors.siteId && <p className="text-sm text-destructive">{form.errors.siteId}</p>}
      </div>

      <div className="space-y-4 pt-2">
        <h2 className="text-base font-semibold">
          {t('organization.departments.form.unitsSectionTitle', {
            count: form.data.units.length,
          })}
        </h2>

        {form.data.units.map((row, index) => (
          <div
            key={row._key}
            className={cn(
              'space-y-4 rounded-lg border p-5',
              index === unitErrorIndex && 'border-destructive',
            )}
          >
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">
                {t('organization.departments.form.unitLabel', { number: index + 1 })}
              </p>
              {activeUnitCount > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveClick(row)}
                >
                  {t('organization.departments.form.removeUnit')}
                </Button>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`unit_name_${row._key}`}>{t('organization.units.form.name')} *</Label>
              <Input
                id={`unit_name_${row._key}`}
                value={row.name}
                onChange={(event) => updateUnit(row._key, 'name', event.target.value)}
                placeholder={t('organization.units.form.namePlaceholder')}
                required
              />
              {index === unitErrorIndex && baseError && (
                <p className="text-sm text-destructive">{baseError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor={`unit_license_${row._key}`}>
                {t('organization.units.form.licenseNumber')}
              </Label>
              <Input
                id={`unit_license_${row._key}`}
                value={row.licenseNumber}
                onChange={(event) => updateUnit(row._key, 'licenseNumber', event.target.value)}
                placeholder={t('organization.units.form.licenseNumberPlaceholder')}
              />
            </div>

            <div
              className={cn(
                'space-y-3 rounded-lg border p-5',
                index === unitErrorIndex && licenseOrAccreditationError && 'border-destructive',
              )}
            >
              <p className="text-sm font-medium">
                {t('organization.units.form.accreditationSection')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                {ACCREDITATION_FIELDS.map(({ key, label }) => (
                  <div key={key} className="space-y-1.5">
                    <Label htmlFor={`unit_${row._key}_${key}`}>{label}</Label>
                    <Input
                      id={`unit_${row._key}_${key}`}
                      value={row.accreditation[key] ?? ''}
                      onChange={(event) =>
                        updateUnit(row._key, 'accreditation', {
                          ...row.accreditation,
                          [key]: event.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>
              {index === unitErrorIndex && licenseOrAccreditationError && (
                <p className="text-sm text-destructive">{licenseOrAccreditationError}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>{t('schedule.loggingSchedule')}</Label>
              <WeeklyScheduleGrid
                value={row.customDaySchedule}
                onChange={(next) => updateUnit(row._key, 'customDaySchedule', next)}
                followParent={row.followParentSchedule}
                onFollowParentChange={(checked) => {
                  updateUnit(row._key, 'followParentSchedule', checked)
                  // Multi-row: one unit toggle can bump while others keep Follow ON.
                  if (parentPreviewFailed) setPreviewRetryNonce((nonce) => nonce + 1)
                }}
                parentPreview={effectiveParentPreview}
                parentPreviewPending={parentPreviewPending}
                parentPreviewFailed={parentPreviewFailed}
                onParentPreviewRetry={() => setPreviewRetryNonce((nonce) => nonce + 1)}
                parentLabel={t('schedule.followSiteSchedule')}
                idPrefix={`unit-schedule-${row._key}`}
              />
            </div>
          </div>
        ))}

        <Button type="button" variant="outline" onClick={addUnit}>
          {t('organization.departments.form.addUnit')}
        </Button>

        {unitErrorIndex === null && baseError && (
          <p className="text-sm text-destructive">{baseError}</p>
        )}

        <Separator />
      </div>

      {!canSubmit && <NoAccessNotice />}

      <div className="flex gap-3 pt-2">
        {canSubmit && (
          <Button type="submit" disabled={form.processing}>
            {form.processing
              ? t('organization.departments.form.submitting')
              : t('organization.departments.form.submit')}
          </Button>
        )}
        <Button type="button" variant="outline" onClick={() => router.visit(departmentsPath())}>
          {t('organization.departments.form.cancel')}
        </Button>
      </div>

      <AlertDialog
        open={pendingRemoval !== null}
        onOpenChange={(open) => !open && setPendingRemoval(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('organization.departments.form.removeUnitTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRemoval?.hasLinkedInstruments
                ? t('organization.departments.form.removeUnitWithInstruments')
                : t('organization.departments.form.removeUnitConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('organization.departments.form.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmExistingUnitRemoval}>
              {t('organization.departments.form.removeUnitAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  )
}

export default DepartmentForm
